import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { SizeBreakpoint, useWindowSize } from '~/common/hooks/useWindowSize'
import { HomeFeedTab } from '~/pages/feed/HomeFeed'
import { useTitle } from '~/common/hooks/useTitle'
import { useBackgroundFetch } from '~/common/hooks/useBackgroundFetch'
import {
  CommunitySortEnum,
  GetCustomerPostsDocument,
  GetCustomerPostsFullDocument,
  GetCustomerPostsFullQuery,
  GetCustomerPostsQuery,
  GetCustomerPostsQueryVariables,
  GetFollowingPostsDocument,
  GetFollowingPostsFullDocument,
  GetFollowingPostsFullQuery,
  GetFollowingPostsQuery,
  GetFollowingPostsQueryVariables,
  GetMyCommunitiesDocument,
  GetMyCommunitiesPostsDocument,
  GetMyCommunitiesPostsFullDocument,
  GetMyCommunitiesPostsFullQuery,
  GetMyCommunitiesPostsQuery,
  GetMyCommunitiesPostsQueryVariables,
  GetNewFeedPostActivityDocument,
  GetVeevaOnlyPostsDocument,
  GetVeevaOnlyPostsFullDocument,
  GetVeevaOnlyPostsFullQuery,
  GetVeevaOnlyPostsQuery,
  GetVeevaOnlyPostsQueryVariables,
  NewAllCommunityActivityAlertDocument,
  NewPublicCommunityActivityAlertDocument,
  SubscriptionInfo,
} from '~/api/generated/graphql'
import { useAuth, useSearchCacheContext } from '~/auth/Auth'
import { FeedActivityList } from '~/pages/feed/FeedActivityList'
import { useLoadObjUsers } from '~/common/hooks/useLoadObjUsers'
import { useApolloClient, useLazyQuery, useQuery, useSubscription } from '@apollo/client'
import { PromptProvider } from '~/contexts/PromptContext'
import { DraftingCommentPostProvider } from '~/contexts/DraftingCommentPostContext'
import { MediaUploadProvider } from '~/contexts/MediaUploadContext'
import { useNavigate } from 'react-router-dom'
import { usePageVisibility } from '~/contexts/PageVisibilityContext'
import { useNewActivity } from '~/contexts/NewActivityContext'
import { setFetchedActivity } from '~/utils'

const HomeFeedActivity = ({
  homeFeedTab,
  pageSize,
  refreshFeed,
  setRefreshFeed,
}: {
  homeFeedTab: HomeFeedTab
  pageSize?: number
  refreshFeed?: boolean
  setRefreshFeed?: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const { breakpoint } = useWindowSize()
  const { isVeevan, authUserId } = useAuth()
  const [postExpanded, setPostExpanded] = useState<boolean>(false)
  const { loadingContent } = useSearchCacheContext()
  useTitle('Feed - Veeva Connect')

  // these queries correspond to cache updates in PostUtils.ts/updateFeedPostFollow.
  // update in both places.
  const {
    previewData: myPostsData,
    loading: myPostsLoading,
    error: myPostsError,
    loadMore: myPostsLoadMore,
    hasMore: myPostsHasMore,
    fullData: fullMyPostsData,
    refetch: refetchMyAll,
  } = useBackgroundFetch<GetMyCommunitiesPostsQuery, GetMyCommunitiesPostsQueryVariables>(
    GetMyCommunitiesPostsDocument,
    GetMyCommunitiesPostsFullDocument,
    {
      variables: { pageSize: pageSize ?? 25 },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    },
    !postExpanded
  )

  const {
    previewData: followingPostsQueryData,
    loading: followingPostsLoading,
    error: followingPostsError,
    loadMore: followingLoadMore,
    hasMore: followingHasMore,
    fullData: fullFollowingData,
    refetch: refetchFollowing,
  } = useBackgroundFetch<GetFollowingPostsQuery, GetFollowingPostsQueryVariables>(
    GetFollowingPostsDocument,
    GetFollowingPostsFullDocument,
    {
      variables: { pageSize: pageSize ?? 25 },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    },
    !postExpanded
  )

  const {
    previewData: customerPostsQueryData,
    loading: customerPostsLoading,
    error: customerPostsError,
    loadMore: customerLoadMore,
    hasMore: customerHasMore,
    fullData: fullCustomerData,
    refetch: refetchCustomer,
  } = useBackgroundFetch<GetCustomerPostsQuery, GetCustomerPostsQueryVariables>(
    GetCustomerPostsDocument,
    GetCustomerPostsFullDocument,
    {
      variables: { pageSize: pageSize ?? 25 },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      skip: !isVeevan,
    },
    !postExpanded
  )

  const {
    previewData: veevaOnlyPostsQueryData,
    loading: veevaOnlyPostsLoading,
    error: veevaOnlyPostsError,
    loadMore: veevaOnlyLoadMore,
    hasMore: veevaOnlyHasMore,
    fullData: fullVeevaOnlyData,
    refetch: refetchVeevaOnly,
  } = useBackgroundFetch<GetVeevaOnlyPostsQuery, GetVeevaOnlyPostsQueryVariables>(
    GetVeevaOnlyPostsDocument,
    GetVeevaOnlyPostsFullDocument,
    {
      variables: { pageSize: pageSize ?? 25 },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      skip: !isVeevan,
    },
    !postExpanded
  )

  const {
    loading: loadingMyPostUsers,
    processedPostIds: processedMyPostIds,
    loadingFirstPage: loadingFirstMyPostUsers,
  } = useLoadObjUsers(myPostsData?.posts, 'postId')
  const {
    loading: loadingFollowingUsers,
    processedPostIds: processedFollowingPostIds,
    loadingFirstPage: loadingFirstFollowingUsers,
  } = useLoadObjUsers(followingPostsQueryData?.posts, 'postId')
  const {
    loading: loadingCustomerUsers,
    processedPostIds: processedCustomerPostIds,
    loadingFirstPage: loadingFirstCustomerUsers,
  } = useLoadObjUsers(customerPostsQueryData?.posts, 'postId')
  const {
    loading: loadingVeevaOnlyUsers,
    processedPostIds: processedVeevaOnlyPostIds,
    loadingFirstPage: loadingFirstVeevaOnlyUsers,
  } = useLoadObjUsers(veevaOnlyPostsQueryData?.posts, 'postId')

  useLoadObjUsers(fullMyPostsData?.posts, 'postId')
  useLoadObjUsers(fullFollowingData?.posts, 'postId')
  useLoadObjUsers(fullVeevaOnlyData?.posts, 'postId')
  useLoadObjUsers(fullCustomerData?.posts, 'postId')

  // This is triggered in the recommended communities header in the home feed.
  useEffect(() => {
    setRefreshFeed?.(r => {
      if (r) {
        void Promise.all([
          refetchMyAll(),
          refetchFollowing(),
          ...(isVeevan ? [refetchCustomer(), refetchVeevaOnly()] : []),
        ]).then()
      }
      return false
    })
  }, [refetchMyAll, refetchFollowing, refreshFeed, setRefreshFeed, refetchCustomer, refetchVeevaOnly, isVeevan])

  const [newActivity, setNewActivity] = useState<SubscriptionInfo[]>([])

  const { setKnownPostIds, setKnownCommentIds } = useNewActivity()

  const setKnownIds = useCallback(
    (
      data:
        | GetMyCommunitiesPostsFullQuery
        | GetFollowingPostsFullQuery
        | GetCustomerPostsFullQuery
        | GetVeevaOnlyPostsFullQuery
        | undefined
    ) => {
      const edges = data?.posts?.edges
      if (!edges?.length) return
      const postIds = edges.map(e => e?.node?.postId).filter(Boolean) as string[]
      if (postIds.length) setKnownPostIds?.(prev => [...new Set([...prev, ...postIds])])
      const commentIds = edges
        .flatMap(e => [
          ...(e?.node?.publicComments?.comments?.map(c => c?.commentId) ?? []),
          ...(e?.node?.discussionComments?.comments?.map(c => c?.commentId) ?? []),
        ])
        .filter(Boolean) as string[]
      if (commentIds.length) setKnownCommentIds?.(prev => [...new Set([...prev, ...commentIds])])
    },
    [setKnownPostIds, setKnownCommentIds]
  )

  useEffect(() => {
    setKnownIds(fullMyPostsData)
  }, [fullMyPostsData, setKnownIds])

  useEffect(() => {
    setKnownIds(fullFollowingData)
  }, [fullFollowingData, setKnownIds])

  useEffect(() => {
    setKnownIds(fullCustomerData)
  }, [fullCustomerData, setKnownIds])

  useEffect(() => {
    setKnownIds(fullVeevaOnlyData)
  }, [fullVeevaOnlyData, setKnownIds])

  const { data: userCommunitiesData } = useQuery(GetMyCommunitiesDocument, {
    variables: { communityFilter: {}, communitySort: [CommunitySortEnum.NameAsc] },
    fetchPolicy: 'cache-only',
  })

  const communityIds = useMemo(() => {
    return userCommunitiesData?.currentUser?.communities?.edges?.map(e => e?.node?.communityId ?? null) || []
  }, [userCommunitiesData])

  const { pageIsVisible } = usePageVisibility()

  const { data: postsOnVisibility } = useQuery(GetMyCommunitiesPostsDocument, {
    fetchPolicy: 'no-cache',
    variables: { pageSize: pageSize ?? 25 },
    skip: !pageIsVisible,
  })

  useEffect(() => {
    if (postsOnVisibility) {
      setFetchedActivity(postsOnVisibility, setNewActivity, myPostsData)
    }
  }, [myPostsData, postsOnVisibility])

  useSubscription(isVeevan ? NewAllCommunityActivityAlertDocument : NewPublicCommunityActivityAlertDocument, {
    variables: { communityIds: communityIds, authUserId: authUserId ?? '' },
    onData: ({ data }) => {
      if (data.data && data.data.newCommunityActivityAlert) {
        const p = data.data.newCommunityActivityAlert
        const activity = [...newActivity]
        if (!activity.some(a => a.objId === p.objId)) {
          activity.push(p)
        }
        setNewActivity(activity)
      }
    },
    skip: !pageIsVisible || globalThis.disableWebsocketServer,
  })

  const [getPostActivity] = useLazyQuery(GetNewFeedPostActivityDocument)
  const client = useApolloClient()
  const navigate = useNavigate()
  const showNewActivity = useCallback(async () => {
    const ids = newActivity.map(a => a.parentId || a.objId)
    const { data: postActivity } = await getPostActivity({ variables: { postIds: ids }, fetchPolicy: 'network-only' }) // update cache so post activity time is reflected correctly
    const oldData = client.readQuery({
      query: GetMyCommunitiesPostsDocument,
      variables: { pageSize: pageSize ?? 25 },
    })

    let newPosts = [...(oldData?.posts?.edges ?? [])]
    const nonDraftIndex = newPosts.findIndex(p => !p?.node?.draft)

    // remove any duplicates, which usually means we are reordering a post higher due to recent activity
    newPosts.splice(nonDraftIndex, 0, ...(postActivity?.posts?.edges ?? []))
    newPosts = newPosts.filter((p1, i, arr) => arr.findIndex(p2 => p1?.node?.postId === p2?.node?.postId) === i)

    const newPostsData = {
      ...oldData,
      posts: {
        ...oldData?.posts,
        pageInfo: {
          ...oldData?.posts?.pageInfo,
          hasNextPage: oldData?.posts?.pageInfo.hasNextPage ?? false,
        },
        edges: newPosts?.slice(0, pageSize ?? 25),
      },
    }

    client.writeQuery({
      query: GetMyCommunitiesPostsDocument,
      variables: { pageSize: pageSize ?? 25 },
      data: newPostsData,
    })
    setNewActivity?.([])
    void navigate('/myfeed')
  }, [client, getPostActivity, navigate, newActivity, pageSize])

  const myPostIds = useMemo(() => {
    if (myPostsData) {
      return myPostsData.posts?.edges
        .map(edge => edge?.node?.postId)
        .filter(id => Boolean(id) && processedMyPostIds?.has(id ?? '')) as string[]
    }
  }, [myPostsData, processedMyPostIds])

  const followingPostIds = useMemo(() => {
    if (followingPostsQueryData) {
      return followingPostsQueryData.posts?.edges
        .map(edge => edge?.node?.postId)
        .filter(id => Boolean(id) && processedFollowingPostIds?.has(id ?? '')) as string[]
    }
  }, [followingPostsQueryData, processedFollowingPostIds])

  const veevaOnlyPostIds = useMemo(() => {
    if (veevaOnlyPostsQueryData) {
      return veevaOnlyPostsQueryData.posts?.edges
        .map(edge => edge?.node?.postId)
        .filter(id => Boolean(id) && processedVeevaOnlyPostIds?.has(id ?? '')) as string[]
    }
  }, [veevaOnlyPostsQueryData, processedVeevaOnlyPostIds])

  const customerPostIds = useMemo(() => {
    if (customerPostsQueryData) {
      return customerPostsQueryData.posts?.edges
        .map(edge => edge?.node?.postId)
        .filter(id => Boolean(id) && processedCustomerPostIds?.has(id ?? '')) as string[]
    }
  }, [customerPostsQueryData, processedCustomerPostIds])

  const { postIds, postsLoading, postsData, loadingFirstPostUsers, loadingPostUsers, loadMore, hasMore, error } =
    useMemo(() => {
      if (homeFeedTab == HomeFeedTab.My) {
        return {
          postIds: myPostIds,
          postsLoading: myPostsLoading,
          postsData: myPostsData,
          loadingFirstPostUsers: loadingFirstMyPostUsers,
          loadingPostUsers: loadingMyPostUsers,
          loadMore: myPostsLoadMore,
          hasMore: myPostsHasMore,
          error: myPostsError,
        }
      } else if (homeFeedTab == HomeFeedTab.Customer) {
        return {
          postIds: customerPostIds,
          postsLoading: customerPostsLoading,
          postsData: customerPostsQueryData,
          loadingFirstPostUsers: loadingFirstCustomerUsers,
          loadingPostUsers: loadingCustomerUsers,
          loadMore: customerLoadMore,
          hasMore: customerHasMore,
          error: customerPostsError,
        }
      } else if (homeFeedTab == HomeFeedTab.VeevaOnly) {
        return {
          postIds: veevaOnlyPostIds,
          postsLoading: veevaOnlyPostsLoading,
          postsData: veevaOnlyPostsQueryData,
          loadingFirstPostUsers: loadingFirstVeevaOnlyUsers,
          loadingPostUsers: loadingVeevaOnlyUsers,
          loadMore: veevaOnlyLoadMore,
          hasMore: veevaOnlyHasMore,
          error: veevaOnlyPostsError,
        }
      } else {
        // (homeFeedTab == HomeFeedTab.Following)
        return {
          postIds: followingPostIds,
          postsLoading: followingPostsLoading,
          postsData: followingPostsQueryData,
          loadingFirstPostUsers: loadingFirstFollowingUsers,
          loadingPostUsers: loadingFollowingUsers,
          loadMore: followingLoadMore,
          hasMore: followingHasMore,
          error: followingPostsError,
        }
      }
    }, [
      homeFeedTab,
      myPostIds,
      myPostsLoading,
      myPostsData,
      loadingFirstMyPostUsers,
      loadingMyPostUsers,
      myPostsLoadMore,
      myPostsHasMore,
      myPostsError,
      followingPostIds,
      followingPostsLoading,
      followingPostsQueryData,
      loadingFirstFollowingUsers,
      loadingFollowingUsers,
      followingLoadMore,
      followingHasMore,
      followingPostsError,
      veevaOnlyPostIds,
      veevaOnlyPostsLoading,
      veevaOnlyPostsQueryData,
      loadingFirstVeevaOnlyUsers,
      loadingVeevaOnlyUsers,
      veevaOnlyLoadMore,
      veevaOnlyHasMore,
      veevaOnlyPostsError,
      customerPostIds,
      customerPostsLoading,
      customerPostsQueryData,
      loadingFirstCustomerUsers,
      loadingCustomerUsers,
      customerLoadMore,
      customerHasMore,
      customerPostsError,
    ])

  const isCondensed = breakpoint <= SizeBreakpoint.lg

  return (
    <PromptProvider>
      <DraftingCommentPostProvider>
        <MediaUploadProvider>
          <div className={`recent-activity ${isCondensed ? 'condensed' : ''}`}>
            <FeedActivityList
              postIds={postIds}
              loading={(postsLoading && !postsData) || loadingContent || loadingFirstPostUsers}
              loadingMore={postsLoading || loadingPostUsers}
              loadMore={loadMore}
              hasMore={hasMore}
              error={error}
              feedOption={homeFeedTab}
              setExpanded={setPostExpanded}
              newActivity={newActivity}
              showNewActivity={showNewActivity}
            />
          </div>
        </MediaUploadProvider>
      </DraftingCommentPostProvider>
    </PromptProvider>
  )
}

export default HomeFeedActivity
